// Video element mounting for both HLS (Cloudflare Stream) and plain MP4
// (legacy Supabase Storage), used by VideoScreen and FeedScreen.
//
// ── Why this exists ──────────────────────────────────────────────────
// 1. It removes an innerHTML XSS sink. Both call sites used to build a player
//    as an HTML STRING with `${video.title}` / `${video.captionUrl}`
//    interpolated unescaped. Everything here is createElement + property
//    assignment, so those values are inert data. That mattered the moment an
//    anon caller could insert a curated_videos row (fixed in migration 030),
//    and it is a hard prerequisite for member uploads.
//
// 2. It resolves what looks like an iOS/HLS conflict but isn't. VideoScreen
//    injects its player synchronously inside the click handler to stay in the
//    iOS user-gesture task; hls.js is inherently async (loadSource ->
//    MANIFEST_PARSED -> play). Those constraints never actually collide:
//    iOS/WKWebView plays HLS NATIVELY and is the only platform with the strict
//    gesture rule, while Android and desktop need hls.js but use sticky
//    activation, where an async play() after any prior interaction is allowed.
//    So the native path stays fully synchronous and hls.js is only ever used
//    where async is fine.

let _nativeHls = null;

// Memoized: constructing a probe element per call is wasteful, and the answer
// cannot change within a session.
export function canPlayNativeHls() {
  if (_nativeHls !== null) return _nativeHls;
  try {
    const el = document.createElement('video');
    _nativeHls = el.canPlayType('application/vnd.apple.mpegurl') !== '';
  } catch {
    _nativeHls = false;
  }
  return _nativeHls;
}

// canPlayType alone is not enough: some Android browsers answer "maybe" and
// then fail to play. Hls.isSupported() alone is wrong in the other direction —
// it loads hls.js on iOS, where it is pure waste and MSE support in WKWebView
// is inconsistent. So: prefer native only on Apple WebKit, or where MSE does
// not exist at all.
export function shouldUseNativeHls() {
  if (!canPlayNativeHls()) return false;
  const ua = navigator.userAgent || '';
  const isAppleWebkit =
    /iP(hone|ad|od)/.test(ua) ||
    // iPadOS in desktop mode reports as Macintosh; touch is the giveaway
    (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document) ||
    (/Safari/.test(ua) && !/Chrome|Chromium|Android/.test(ua));
  return isAppleWebkit || typeof window.MediaSource === 'undefined';
}

const isHls = (src) => typeof src === 'string' && src.includes('.m3u8');

// ⚠️ MUST be the full hls.js build (~160KB gz), NOT hls.js/light (~106KB gz).
// Cloudflare Stream serves DEMUXED audio — the master manifest declares
// `#EXT-X-MEDIA:TYPE=AUDIO,GROUP-ID="group_audio"` and every variant references
// it — and the light build compiles out alternate-audio support. The result is
// video that plays perfectly with NO SOUND and a greyed-out volume control,
// which looks like a broken upload rather than a player-config problem.
//
// Do not "optimise" this back to light on the grounds that we only use external
// <track> captions. Measured against a real Cloudflare manifest:
//     hls.light -> hls.audioTracks = 0, 0 audio bytes decoded
//     hls (full) -> hls.audioTracks = 1 ["original"], audio decodes
// Grepping the light bundle is NOT a valid check: the AudioTrackController /
// AudioStreamController symbols are present in both, but a build flag disables
// the path in light.
//
// Warm the dynamic import so the chunk is already in the module cache by the
// time someone taps play. Safe to call repeatedly; no-op on native.
export function preloadHls() {
  if (shouldUseNativeHls()) return;
  import('hls.js').catch(() => {});
}

/**
 * Mount a player into `container`, replacing whatever is there.
 * Returns a cleanup function — CALL IT. hls.js holds XHRs, timers and a worker
 * that leak otherwise (the old innerHTML code never tore anything down, which
 * was harmless only because a bare <video> needs no teardown).
 */
export function mountVideo(container, { src, poster, captionUrl, captionLabel = 'English' } = {}) {
  if (!container || !src) return () => {};

  container.textContent = '';

  const video = document.createElement('video');
  video.className = 'absolute inset-0 w-full h-full';
  video.controls = true;
  video.autoplay = true;
  video.playsInline = true;
  // WKWebView historically honours the attribute rather than the property
  video.setAttribute('playsinline', '');
  video.style.background = '#000';
  if (poster) video.poster = poster;

  if (captionUrl) {
    // A cross-origin <track> is dropped unless the media element is CORS-enabled.
    // The document is dsp.cafe while captions live on Supabase (and video will
    // live on Cloudflare), so this is always cross-origin. Both send
    // Access-Control-Allow-Origin: *, so this is safe on either path.
    video.crossOrigin = 'anonymous';

    const track = document.createElement('track');
    track.kind = 'subtitles';
    track.src = captionUrl;
    track.srclang = 'en';
    track.label = captionLabel;
    track.default = true;
    video.appendChild(track);
  }

  let hls = null;

  if (!isHls(src) || shouldUseNativeHls()) {
    // Synchronous path — plain MP4, or HLS on a platform that plays it natively.
    // Nothing here awaits, so the iOS user-gesture context is preserved.
    video.src = src;
    container.appendChild(video);
    const p = video.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } else {
    // hls.js path. Append synchronously so layout is stable, then attach once
    // the library resolves. Sticky activation makes the deferred play() fine on
    // every platform that lands here.
    container.appendChild(video);
    import('hls.js')
      .then(({ default: Hls }) => {
        if (!container.contains(video)) return; // unmounted while loading
        if (!Hls || !Hls.isSupported()) {
          video.src = src; // last-ditch: let the browser try
          return;
        }
        hls = new Hls({ enableWorker: true });
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          const p = video.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        });
        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (!data?.fatal) return;
          // Fatal network/media errors are recoverable once each; anything else
          // tears down rather than looping.
          if (data.type === 'networkError') hls.startLoad();
          else if (data.type === 'mediaError') hls.recoverMediaError();
          else { hls.destroy(); hls = null; }
        });
      })
      .catch(() => {
        video.src = src;
      });
  }

  return () => {
    try { hls?.destroy(); } catch { /* already torn down */ }
    hls = null;
    try {
      video.pause();
      video.removeAttribute('src');
      video.load();
    } catch { /* element already detached */ }
    if (container) container.textContent = '';
  };
}
